import React, { useCallback, useEffect, useMemo, useRef, useId } from "react";
import "../styles/qty-control.css"; // (voir CSS plus bas)

export default function QtyControlPro({
  value = 1,
  min = 1,
  max,
  step = 1,
  disabled = false,
  readOnly = false,
  onChange,
  size = "md", // "sm" | "md" | "lg"
  className = "",
  id,
  name,
  "aria-label": ariaLabel = "Quantité",
  title,
  autoRepeat = true,      // appui long activé
  repeatDelay = 280,      // délai avant répétition (ms)
  repeatInterval = 60,    // interval de répétition (ms)
}) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  // ---- sizes ----
  const S = useMemo(() => {
    const map = {
      sm: { h: 34, w: 48, f: 14, btn: 36, icon: 18 },
      md: { h: 40, w: 56, f: 15, btn: 44, icon: 20 },
      lg: { h: 48, w: 64, f: 16, btn: 52, icon: 22 },
    };
    return map[size] || map.md;
  }, [size]);

  // ---- helpers ----
  const toNumber = (n) => (n === "" || n === null || n === undefined ? NaN : Number(n));

  const clamp = useCallback(
    (n) => {
      const num = toNumber(n);
      const base = toNumber(min);
      const ceil = typeof max === "number" ? max : Infinity;

      const raw = Number.isFinite(num) ? num : (Number.isFinite(base) ? base : 1);

      // arrondi au pas (ancré à min)
      const effectiveStep = Math.max(1e-9, step || 1);
      const ref = Number.isFinite(base) ? base : 0;
      const stepped = ref + Math.round((raw - ref) / effectiveStep) * effectiveStep;

      const low = Number.isFinite(base) ? base : -Infinity;
      return Math.min(ceil, Math.max(low, stepped));
    },
    [min, max, step]
  );

  const canDec = !disabled && !(typeof min === "number" && value <= min);
  const canInc = !disabled && !(typeof max === "number" && value >= max);
  const blocked = disabled || readOnly || (typeof min === "number" && typeof max === "number" && min >= max);

  // ---- actions ----
  const doDec = () => !blocked && onChange?.(clamp((value ?? min ?? 1) - step));
  const doInc = () => !blocked && onChange?.(clamp((value ?? min ?? 1) + step));

  const onInput = (e) => {
    if (readOnly) return;
    onChange?.(clamp(e.target.value));
  };

  const onBlur = (e) => {
    if (readOnly) return;
    const fixed = clamp(e.target.value);
    if (fixed !== value) onChange?.(fixed);
  };

  const onKeyDown = (e) => {
    if (readOnly) return;
    switch (e.key) {
      case "ArrowUp": e.preventDefault(); doInc(); break;
      case "ArrowDown": e.preventDefault(); doDec(); break;
      case "Home":
        if (typeof min === "number") { e.preventDefault(); onChange?.(clamp(min)); }
        break;
      case "End":
        if (typeof max === "number") { e.preventDefault(); onChange?.(clamp(max)); }
        break;
      case "PageUp": e.preventDefault(); onChange?.(clamp((value ?? min ?? 1) + step * 5)); break;
      case "PageDown": e.preventDefault(); onChange?.(clamp((value ?? min ?? 1) - step * 5)); break;
      default: break;
    }
  };

  const onWheel = (e) => {
    // Évite les changements accidentels lors du scroll
    if (e.currentTarget === document.activeElement) e.preventDefault();
  };

  // ---- auto-repeat (appui long) ----
  const holdTimerRef = useRef(null);
  const repeatTimerRef = useRef(null);

  const startHold = (fn) => {
    if (!autoRepeat || blocked) return fn();
    clearTimeout(holdTimerRef.current);
    clearInterval(repeatTimerRef.current);
    holdTimerRef.current = setTimeout(() => {
      repeatTimerRef.current = setInterval(fn, repeatInterval);
    }, repeatDelay);
    fn(); // 1er clic immédiat
  };

  const stopHold = () => {
    clearTimeout(holdTimerRef.current);
    clearInterval(repeatTimerRef.current);
  };

  useEffect(() => stopHold, []); // cleanup on unmount

  return (
    <div
      className={`qtypro d-inline-flex align-items-center ${blocked ? "is-disabled" : ""} ${className}`}
      role="group"
      aria-label={ariaLabel}
      aria-disabled={blocked ? "true" : undefined}
      title={title}
      style={{ height: S.h }}
    >
      <button
        type="button"
        className="qtypro-btn qtypro-btn--left"
        aria-label="Diminuer"
        aria-controls={inputId}
        onMouseDown={() => startHold(doDec)}
        onTouchStart={() => startHold(doDec)}
        onMouseUp={stopHold}
        onMouseLeave={stopHold}
        onTouchEnd={stopHold}
        onClick={(e) => e.preventDefault()}
        disabled={!canDec || blocked}
        style={{ width: S.btn, minWidth: S.btn, fontSize: S.icon }}
      >
        −
      </button>

      <input
        id={inputId}
        name={name}
        className="qtypro-input"
        type="text"           /* meilleur clavier numérique mobile */
        inputMode="numeric"
        pattern="[0-9]*"
        value={String(value ?? "")}
        onChange={onInput}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onWheel={onWheel}
        aria-label={ariaLabel}
        role="spinbutton"
        aria-valuenow={Number.isFinite(toNumber(value)) ? Number(value) : undefined}
        aria-valuemin={typeof min === "number" ? min : undefined}
        aria-valuemax={typeof max === "number" ? max : undefined}
        readOnly={readOnly}
        disabled={disabled}
        style={{ width: S.w, fontSize: S.f }}
      />

      <button
        type="button"
        className="qtypro-btn qtypro-btn--right"
        aria-label="Augmenter"
        aria-controls={inputId}
        onMouseDown={() => startHold(doInc)}
        onTouchStart={() => startHold(doInc)}
        onMouseUp={stopHold}
        onMouseLeave={stopHold}
        onTouchEnd={stopHold}
        onClick={(e) => e.preventDefault()}
        disabled={!canInc || blocked}
        style={{ width: S.btn, minWidth: S.btn, fontSize: S.icon }}
      >
        +
      </button>
    </div>
  );
}
